import {
  DocumentAttributes,
  Section,
  ItemSet,
  DocumentTokenInformation,
  UnmanagedDocument,
  IBookmark,
  DocumentInjectedData,
} from ".";
import { PatientProfileInformation } from "../patient";
import { FormatReference } from "../format";
import { HisInformation } from "../his";
import { DesignatedDoctorInformation } from "../user";
import { ChainedDocumentIdentifier } from "./ChainedDocumentIdentifier";
import { ReferenceFileInformation } from "./ReferenceFileInformation";
import { InstitutionItem } from "../institution";

/**
 * ★★フロントエンドでのデータの解釈の概要★★
 * ドキュメントは必ず1つ以上のフォーマットから成り立っている
 * フォーマットは必ず1つのItemSetを持つ
 * フォーマットは必ず1つ以上のまとまり（Section）から成り立っている
 * まとまり（Section）は、現実的には文書内のページとして表現されている
 */
export class Document {
  constructor(
    public documentId: number,
    public edition: number,
    public attributes: DocumentAttributes,
    public itemSets: ItemSet[],
    public sections: Section[],
    public extraFormats: FormatReference[],
    public hisInformation: HisInformation,
    public referenceFiles: ReferenceFileInformation[],
    public patientProfile: PatientProfileInformation,
    public designatedDoctorInformations: DesignatedDoctorInformation[],
    public isNewHisInformationApplied: boolean,
    public editToken: DocumentTokenInformation,
    public createToken: DocumentTokenInformation,
    public writeToken: DocumentTokenInformation,
    public selectedInstitution: InstitutionItem,
    public unmanagedInformation: UnmanagedDocument,
    public bookmarks: IBookmark[],
    public chainableFormatOriginalIds: number[],
    public chainedFormatOriginalIds: number[],    // フロントエンド内でのみ扱う一時的な情報.(新規でのみ使い最終的に登録時はchainDocumentsにその情報が設定される.)
    public chainedDocuments: ChainedDocumentIdentifier[],
    public chainDocuments: Document[],
    public shouldRestrictExternalScript: boolean,    // フロントエンド内でのみ扱う情報.(外部処理の実行を制限するかどうか)
    public allowedExternalScripts: string[],    // フロントエンド内でのみ扱う情報.(実行を許可する外部処理を指定)
    public injectedData: DocumentInjectedData | null = null,
  ) {
  }

  clone(): Document {
    return new Document(
      this.documentId,
      this.edition,
      this.attributes.clone(),
      this.itemSets.concat(),     // 配列要素のdeepcopyはしない.(追加する際にdeepcopyしたインスタンスを格納する事.)
      this.sections.concat(),     // 配列要素のdeepcopyはしない.(追加する際にdeepcopyしたインスタンスを格納する事.)
      this.extraFormats.concat(), // 配列要素のdeepcopyはしない.(追加する際にdeepcopyしたインスタンスを格納する事.)
      this.hisInformation,
      this.referenceFiles,
      this.patientProfile,
      this.designatedDoctorInformations,
      this.isNewHisInformationApplied,
      this.editToken,
      this.createToken,
      this.writeToken,
      this.selectedInstitution,
      this.unmanagedInformation,
      this.bookmarks,
      this.chainableFormatOriginalIds,
      this.chainedFormatOriginalIds,
      this.chainedDocuments,
      this.chainDocuments,
      this.shouldRestrictExternalScript,
      this.allowedExternalScripts,
      this.injectedData,
    );
  }

  forceOverwrite(source: Document): void {
    this.documentId = source.documentId;
    this.edition = source.edition;
    this.attributes.forceOverwrite(source.attributes);
    // itemSets
    // sections
    // extraFormats
    this.hisInformation = source.hisInformation;
    // referenceFiles
    this.patientProfile = source.patientProfile;
    this.designatedDoctorInformations = source.designatedDoctorInformations;
    this.isNewHisInformationApplied = source.isNewHisInformationApplied;
    this.editToken = source.editToken;
    this.createToken = source.createToken;
    this.writeToken = source.writeToken;
    this.selectedInstitution = source.selectedInstitution;
    this.unmanagedInformation = source.unmanagedInformation;
    this.bookmarks = source.bookmarks;
    this.injectedData = source.injectedData;
  }

  freezeItems(): void {
    for (const itemSet of this.itemSets) {
      if (!itemSet) { continue; }
      itemSet.freezeItems();
    }
  }
}
